import { useMemo } from "react";
import { Loader } from "@themes/pollinator/ui/Loader";
import {
  useFetchMultipleInvitationRecords,
  type InvitationRecord,
  type Invitation,
} from "@core/features/invitation";
import { TrackingMetrics } from "./TrackingMetrics";

/** Signup method data shape for tracking metrics */
type SignupMethodData = {
  value: number;
  label: string;
};

export type Props = {
  invitations: Invitation[];
};

// Map claimed_via values to display labels
const SIGNUP_METHOD_LABELS: Record<string, string> = {
  direct_link: "Direct invite link",
  code_entry: "Code entry",
  lookup: "Organization lookup",
};

function computeSignupMethodData(
  records: InvitationRecord[]
): SignupMethodData[] {
  const claimedRecords = records.filter((r) => r.claimed_status === "claimed");
  const methodCounts: Record<string, number> = {};

  claimedRecords.forEach((record) => {
    const method = record.claimed_via || "unknown";
    methodCounts[method] = (methodCounts[method] || 0) + 1;
  });

  return Object.entries(methodCounts).map(([method, count]) => ({
    value: count,
    label: SIGNUP_METHOD_LABELS[method] || method,
  }));
}

function computeInvitationBreakdownData(
  records: InvitationRecord[],
  invitations: Invitation[]
): SignupMethodData[] {
  const claimedRecords = records.filter((r) => r.claimed_status === "claimed");
  const shortcodeCounts: Record<string, number> = {};

  claimedRecords.forEach((record) => {
    const shortcode = record.invitation_shortcode || "unknown";
    shortcodeCounts[shortcode] = (shortcodeCounts[shortcode] || 0) + 1;
  });

  // Create a map of shortcode to program name
  const shortcodeToName: Record<string, string> = {};
  invitations.forEach((inv) => {
    shortcodeToName[inv.shortcode] = inv.program_name || inv.shortcode;
  });

  return Object.entries(shortcodeCounts).map(([shortcode, count]) => ({
    value: count,
    label: shortcodeToName[shortcode] || shortcode,
  }));
}

export function TrackingMetricsContainer({ invitations }: Props) {
  const invitationIds = useMemo(
    () => invitations.map((inv) => inv.id),
    [invitations]
  );

  const { data: records, isLoading } = useFetchMultipleInvitationRecords({
    invitationIds,
    enabled: invitationIds.length > 0,
  });

  const { claimedSeats, totalSeats, signupMethodData, invitationBreakdownData } =
    useMemo(() => {
      if (!records || records.length === 0) {
        return {
          claimedSeats: 0,
          totalSeats: 0,
          signupMethodData: [],
          invitationBreakdownData: [],
        };
      }

      // Calculate total seats (sum of all max_uses, treat null as unlimited = 100)
      const total = invitations.reduce(
        (sum, inv) => sum + (inv.max_uses ?? 100),
        0
      );

      const claimed = records.filter(
        (r) => r.claimed_status === "claimed"
      ).length;
      const signupMethods = computeSignupMethodData(records);

      // Only compute invitation breakdown if there are multiple invitations
      const invBreakdown =
        invitations.length > 1
          ? computeInvitationBreakdownData(records, invitations)
          : [];

      return {
        claimedSeats: claimed,
        totalSeats: total,
        signupMethodData: signupMethods,
        invitationBreakdownData: invBreakdown,
      };
    }, [records, invitations]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <TrackingMetrics
      claimedSeats={claimedSeats}
      totalSeats={totalSeats}
      signupMethodData={signupMethodData}
      invitationBreakdownData={
        invitationBreakdownData.length > 0 ? invitationBreakdownData : undefined
      }
    />
  );
}

export default TrackingMetricsContainer;
